import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import './Calendar.scss';
// variables & functions
import { bsMonths, bsMonthsEnglish, nepaliNumbers, WEEKDAYSOBJECT } from './variables';
import { currentYear, getYears } from './dates';
import { BikramSambatConverter } from './bsdate-converter';
import NepaliDate from 'nepali-date-converter';
import DateComp from './components/Date';

export const Calendar = ({
  events,
  language,
  mode,
  onDateClicked,
  startDate,
  dayClickBehaviour
}) => {
  const tempDate = new Date(startDate);
  const [selectedDate, setSelectedDate] = useState();
  const date = new BikramSambatConverter();
  const nepaliDate = date.engToNepali(
    tempDate.getFullYear(),
    tempDate.getMonth() + 1,
    tempDate.getDate()
  );

  const WEEKDAYS = useMemo(() => {
    return WEEKDAYSOBJECT[language];
  }, [language]);
  // let nepaliDate = ;
  let englishDateObj = new NepaliDate(
    `${nepaliDate.year}-${nepaliDate.nmonth}-${nepaliDate.date}`
  ).getAD();
  let activeDate = `${englishDateObj.year}-${('0' + englishDateObj.month).slice(-2)}-${(
    '0' + englishDateObj.date
  ).slice(-2)}`;
  const [year, setYear] = useState(nepaliDate.year);
  const [month, setMonth] = useState(nepaliDate.nmonth);
  const [days, setDays] = useState([]);
  const [clickedDates, setClickedDates] = useState([]);

  const yearInfo = currentYear(year);
  const currMonth = yearInfo[month - 1];
  const years = getYears();

  const handleDateClick = (day) => {
    if (dayClickBehaviour === 'multiple') {
      const tempClickedDates = [...clickedDates];
      const index = tempClickedDates.findIndex((clickedDate) => clickedDate.date === day.date);
      if (index !== -1) {
        tempClickedDates.splice(index, 1);
        setClickedDates(tempClickedDates);
        return onDateClicked(tempClickedDates);
      }
      tempClickedDates.push(day);
      setClickedDates(tempClickedDates);
      return onDateClicked(tempClickedDates);
    }
    setClickedDates([day]);
    setSelectedDate(day.date);
    return onDateClicked(day);
  };

  const handleMonthChange = (e) => {
    clearDates();
    setMonth(e.target.value * 1);
  };
  const handleYearChange = (e) => {
    clearDates();
    setYear(e.target.value * 1);
  };
  const clearDates = () => {
    setClickedDates([]);
  };

  const goToNextMonth = () => {
    clearDates();
    if (month > 11) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };
  const goToPrevMonth = () => {
    clearDates();
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const createDaysForCurrentMonth = useCallback(() => {
    return [...Array(currMonth.endDate)].map((day, index) => {
      let nepaliDate = `${year}-${month}-${index + 1}`;
      let englishDateObj = new NepaliDate(nepaliDate).getAD();
      let englishDate = `${englishDateObj.year}-${('0' + (englishDateObj.month + 1)).slice(-2)}-${(
        '0' + englishDateObj.date
      ).slice(-2)}`;
      let dayObj = {
        date_np: nepaliDate,
        date: englishDate,
        dayOfMonth: index + 1,
        day: (currMonth.weekStart + index) % 7,
        isCurrentMonth: true
      };
      if (events && events[englishDate]?.length > 0) {
        const dayEvents = events[englishDate];
        return {
          ...dayObj,
          events: dayEvents,
          noOfEvents: dayEvents.length
        };
      }
      return dayObj;
    });
  }, [currMonth.endDate, currMonth.weekStart, events, month, year]);

  useEffect(() => {
    let currentMonthDays = createDaysForCurrentMonth();
    setDays(currentMonthDays);
  }, [createDaysForCurrentMonth]);

  useEffect(() => {
    days.forEach((day) => {
      if (day.date === activeDate) {
        handleDateClick(day);
      }
    });
  }, []);

  return (
    <main className={`calendar-container`}>
      <div>
        <div className="yearSelectContainer">
          {mode === 'yearly' && (
            <select className="select" id="year" value={year} onChange={handleYearChange}>
              {years.map((year) => (
                <option key={year} value={year}>
                  {language === 'ne' ? nepaliNumber(year) : year}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="monthSelectContainer">
          <button className="btn" onClick={goToPrevMonth}>
            {language === 'ne' ? 'अघिल्लो महिना' : 'Previous month'}
          </button>
          <select
            className="select border-primary bg-white  rounded"
            id="month"
            value={month}
            onChange={handleMonthChange}>
            {language === 'ne'
              ? bsMonths.map((month, i) => (
                  <option key={month} value={i + 1}>
                    {month}
                  </option>
                ))
              : bsMonthsEnglish.map((month, i) => (
                  <option key={month} value={i + 1}>
                    {month}
                  </option>
                ))}
          </select>
          <button className="btn" onClick={goToNextMonth}>
            {language === 'ne' ? 'अर्को महिना' : 'Next month'}
          </button>
        </div>
      </div>
      <div className="calender-container">
        {/* calendar grid header */}
        <ul className="weekdays">
          {WEEKDAYS?.map((weekday, index) => (
            <li key={index}>{weekday}</li>
          ))}
        </ul>
        {/* calendar grid */}
        <ul className="days">
          {/* black space */}
          {Array.apply(null, { length: currMonth.weekStart }).map((e, i) => (
            <li key={i}> </li>
          ))}
          {days
            .sort((a, b) => (a.id > b.id ? 1 : 0))
            .map((day, dayIndex) => {
              let noOfEventsClass = '';
              if (day.noOfEvents > 0) {
                if (day.noOfEvents <= 5) {
                  noOfEventsClass = 'low-events';
                } else if (day.noOfEvents <= 10) {
                  noOfEventsClass = 'medium-events';
                } else {
                  noOfEventsClass = 'high-events';
                }
              }
              return (
                <DateComp
                  key={dayIndex}
                  day={day}
                  activeDate={activeDate}
                  selectedDate={selectedDate}
                  noOfEventsClass={noOfEventsClass}
                  handleDateClick={handleDateClick}
                  language={language}
                  dayIndex={dayIndex}
                />
              );
            })}
        </ul>
      </div>
    </main>
  );
};

Calendar.defaultProps = {
  language: 'en',
  mode: 'monthly',
  onDateClicked: () => {},
  dayClickBehaviour: 'single',
  startDate: new Date()
};

Calendar.propTypes = {
  language: PropTypes.string,
  mode: PropTypes.string,
  onDateClicked: PropTypes.func.isRequired,
  dayClickBehaviour: PropTypes.string,
  startDate: PropTypes.any
};

export const nepaliNumber = (number) => {
  const strNum = String(number);
  let strNepNum = '';
  for (let i = 0; i < strNum.length; i++) {
    strNepNum += nepaliNumbers[parseInt(strNum.substr(i, 1))];
  }
  return strNepNum;
};
